import React, { useEffect, useState } from 'react';
import { apiClient } from '../../lib/api';
import { useToast } from '../../hooks/useToast';
import { formatDateOnly } from '../../utils/dateUtils';
import { BRIEF_FIELD_LABELS, BRIEF_SOURCE_LABELS, briefValueLabel } from '../../constants/brief';
import type { CampaignBrief } from '../../types/pipeline';

/** The project's brief on the pipeline card: each field with its source, Draft or Approved,
 * and the message plan once approved. */
export const ProjectBriefBlock: React.FC<{ projectId: string }> = ({ projectId }) => {
  const { showToast } = useToast();
  const [brief, setBrief] = useState<CampaignBrief | null>(null);

  useEffect(() => {
    let stale = false;
    setBrief(null);
    apiClient.getProjectBrief(projectId).then(
      (loaded) => { if (!stale) setBrief(loaded); },
      (error) => {
        console.error('Project brief load failed:', error);
        if (!stale) showToast("Couldn't load the brief. Reopen the card to try again.", 'error');
      },
    );
    return () => { stale = true; };
  }, [projectId, showToast]);

  if (!brief) return null;
  const approvedAt = brief.confirmed_at;

  return (
    <div>
      <div className="flex items-center gap-2 mb-1">
        <h4 className="text-13 font-semibold text-neutral-700">Brief</h4>
        <span className={`text-xs px-2 py-0.5 rounded-full ${approvedAt ? 'bg-green-50 text-green-700' : 'bg-neutral-100 text-neutral-600'}`}>
          {approvedAt ? `Approved ${formatDateOnly(approvedAt)}` : 'Draft'}
        </span>
      </div>
      <dl className="text-xs text-neutral-700 space-y-1">
        {brief.fields.map(({ name, value, source }) => (
          <div key={name} className="flex gap-2">
            <dt className="w-16 shrink-0 text-neutral-500">{BRIEF_FIELD_LABELS[name]}</dt>
            <dd className="min-w-0">
              {value ? briefValueLabel(name, value) : <span className="text-neutral-400">Not set</span>}
              {source && <span className="text-neutral-400"> · {BRIEF_SOURCE_LABELS[source]}</span>}
            </dd>
          </div>
        ))}
      </dl>
      {brief.plan_summary && <p className="text-xs text-neutral-700 mt-1.5"><span className="font-semibold">Message plan:</span> {brief.plan_summary}</p>}
    </div>
  );
};
